// Comprehensive Texas family law data based on Custody X Change and official Texas sources
const texasData = {
  'texas': {
    name: 'Texas',
    overview: {
      keyPoints: [
        'Texas uses "conservatorship" terminology instead of traditional custody language',
        'Joint managing conservatorship is presumed to be in the best interest of the child',
        'Texas uses "possession and access" instead of visitation terminology',
        'The state has detailed Standard Possession Order schedules for non-primary conservators',
        'Texas Family Code presumes parents should have frequent and continuing contact with children',
        'The state requires parenting plans in cases involving children',
        'Texas has specific provisions for geographic restrictions on the child\'s residence',
        'Mediation is required in most contested conservatorship cases'
      ],
      uniqueFeatures: [
        'Texas uses unique legal terminology: "conservatorship" instead of custody and "possession" instead of visitation',
        'The state has a detailed Standard Possession Order that serves as the default schedule',
        'Texas allows for expanded Standard Possession Orders for parents who live within 100 miles',
        'The state has specific provisions for parents living more than 100 miles apart',
        'Texas recognizes both joint managing conservatorship and sole managing conservatorship',
        'The state has mandatory mediation requirements for most contested cases',
        'Texas allows for geographic restrictions limiting where the child can live',
        'The state has specialized family violence court procedures'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Texas for at least 6 months before filing, or Texas must be the child\'s home state under UCCJEA',
      filing: 'File in the district court in the county where the child has lived for the past 6 months or where either parent resides',
      paternity: 'Unmarried fathers can establish paternity through acknowledgment of paternity, genetic testing, court order, or presumption',
      documentation: 'Required documents include petition, citation, standing order, and proposed parenting plan',
      uniqueRules: [
        'Texas requires a standing order to be served that prohibits certain actions during the case',
        'Mandatory mediation is required before trial in most contested conservatorship cases',
        'Guardian ad litem or attorney ad litem may be appointed to represent the child\'s interests',
        'Texas allows for temporary orders to address immediate custody and support needs',
        'The state requires disclosure of criminal history and protective orders'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Texas uses "conservatorship" - Joint Managing Conservatorship (JMC) or Sole Managing Conservatorship (SMC)',
      legalCustody: 'Managing conservatorship includes the right to make major decisions about the child\'s welfare',
      jointCustody: 'Joint Managing Conservatorship is presumed to be in the child\'s best interest and allows both parents to share decision-making',
      visitation: 'Called "possession and access" - includes specific schedules detailed in the Standard Possession Order',
      specialProvisions: [
        'Supervised possession may be ordered when child safety is a concern',
        'Standard Possession Order provides detailed schedules for different circumstances',
        'Expanded Standard Possession Order available for parents living within 100 miles',
        'Modified possession schedules for parents living more than 100 miles apart',
        'Geographic restrictions may limit where the child can live',
        'Grandparent access rights are recognized under specific circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Conservatorship arrangement (joint managing or sole managing)',
        'Rights and duties of each conservator',
        'Possession and access schedule including weekends, weekdays, and holidays',
        'Holiday and vacation possession schedules',
        'Transportation arrangements and costs',
        'Communication guidelines between parents and with children',
        'Decision-making authority for education, healthcare, and other major decisions',
        'Geographic restrictions on the child\'s residence',
        'Child support obligations',
        'Dispute resolution procedures including mediation requirements'
      ],
      tips: [
        'Texas courts favor arrangements that follow the Standard Possession Order framework',
        'Be specific about pickup and drop-off times and locations',
        'Address how to handle missed possession due to emergencies or conflicts',
        'Include provisions for the child\'s participation in extracurricular activities',
        'Consider Texas\' size and geography when planning possession schedules',
        'Address communication methods and frequency between parent and child',
        'Include provisions for make-up possession time when scheduled time is missed',
        'Plan for extended summer possession as provided in Standard Possession Order'
      ]
    },
    childSupport: {
      guidelines: 'Texas uses a percentage of obligor\'s net income model with specific percentages based on number of children',
      factors: [
        'Net monthly income of the paying parent (obligor)',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment',
        'Extraordinary medical expenses',
        'Other court-ordered support obligations',
        'Extended possession time with paying parent may affect support',
        'Special needs of the children'
      ],
      calculation: 'Texas Child Support Guidelines using percentage of net income: 20% for 1 child, 25% for 2 children, 30% for 3 children, 35% for 4 children, 40% for 5+ children',
      modifications: 'Material and substantial change in circumstances required, or three years have passed since last order',
      enforcement: 'Strong enforcement through Texas Attorney General Child Support Division including wage garnishment, asset seizure, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Texas uses a percentage-of-income model where child support is calculated as a percentage of the paying parent\'s net monthly income.',
          steps: [
            'Calculate the obligor parent\'s gross monthly income from all sources',
            'Subtract allowable deductions to determine net monthly income',
            'Apply the appropriate percentage based on number of children',
            'Add health insurance premiums for the children',
            'Add childcare expenses necessary for employment',
            'Add extraordinary medical expenses',
            'Determine final monthly support obligation'
          ]
        },
        percentages: {
          description: 'Texas uses standard percentages of net monthly income based on number of children',
          schedule: [
            '1 child: 20% of net monthly income',
            '2 children: 25% of net monthly income',
            '3 children: 30% of net monthly income',
            '4 children: 35% of net monthly income',
            '5 or more children: 40% of net monthly income'
          ],
          caps: [
            'Guidelines apply to net income up to $9,200 per month',
            'For income above cap, court has discretion for additional support',
            'Multiple family situations may affect percentages'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime',
            'Self-employment income',
            'Investment income and dividends',
            'Rental property income',
            'Retirement and pension benefits',
            'Social Security benefits',
            'Unemployment compensation',
            'Workers\' compensation benefits',
            'Disability benefits',
            'Military pay and allowances',
            'Spousal maintenance received'
          ],
          excluded: [
            'Means-tested public assistance',
            'Foster care payments',
            'Child support received for other children',
            'Supplemental Security Income (SSI)',
            'Income of new spouse',
            'Gifts and inheritances (unless regular income)'
          ]
        },
        deductions: {
          allowable: [
            'Federal income taxes',
            'State income taxes (Texas has no state income tax)',
            'Social Security taxes',
            'Medicare taxes',
            'Union dues',
            'Health insurance premiums for obligor',
            'Court-ordered child support for other children',
            'Court-ordered spousal maintenance'
          ]
        },
        possessionAdjustment: {
          description: 'Texas may adjust child support for extended possession time',
          threshold: 'Typically applies when obligor has possession more than Standard Possession Order provides',
          factors: [
            'Actual possession time documented',
            'Additional costs during extended possession',
            'Reduced costs to primary conservator',
            'Best interests of the child'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Health insurance premiums for the children',
            responsibility: 'Court may order either or both parents to provide health insurance',
            allocation: 'Premium costs typically added to basic support obligation'
          },
          childcare: {
            description: 'Childcare expenses reasonably necessary for employment',
            allocation: 'May be added to basic support or shared between parents',
            requirements: 'Must be work-related necessity'
          },
          extraordinaryMedical: {
            description: 'Medical expenses not covered by insurance',
            examples: [
              'Dental and orthodontic treatment',
              'Vision care and corrective lenses',
              'Mental health treatment',
              'Physical therapy',
              'Prescription medications',
              'Medical equipment and supplies'
            ],
            allocation: 'Typically shared proportionally between parents'
          },
          extracurricular: {
            description: 'Costs for children\'s activities and enrichment',
            note: 'Not automatically included - must be agreed upon or court-ordered',
            examples: [
              'Sports teams and equipment',
              'Music lessons and instruments',
              'Dance or martial arts classes',
              'Summer camps',
              'Educational activities and tutoring'
            ]
          }
        },
        modifications: {
          grounds: [
            'Material and substantial change in circumstances',
            'Three years have passed since last order',
            'Significant change in income (typically 20% or more)',
            'Change in possession schedule',
            'Change in health insurance or childcare costs',
            'Change in number of children'
          ],
          process: [
            'File Motion to Modify Child Support',
            'Serve other parent with motion and supporting documents',
            'Provide updated income and expense information',
            'Attend mediation if required',
            'Present case at court hearing'
          ],
          timeline: 'Modifications typically effective from date of filing motion'
        },
        enforcement: {
          methods: [
            'Income withholding (wage garnishment)',
            'Asset seizure and bank levies',
            'Tax refund intercept',
            'License suspension (driver\'s, professional, recreational)',
            'Credit bureau reporting',
            'Contempt of court proceedings',
            'Property liens',
            'Passport denial',
            'Lottery and gaming intercept'
          ],
          interest: 'Unpaid support accrues interest at 6% per annum',
          agency: 'Texas Attorney General Child Support Division handles enforcement'
        }
      }
    },
    standardPossessionOrder: {
      description: 'Texas Standard Possession Order provides detailed possession schedules',
      regular: {
        weekends: 'First, third, and fifth weekends of each month from Friday 6:00 PM to Sunday 6:00 PM',
        weekdays: 'Thursday from 6:00 PM to Friday morning (when school resumes) on weekends when non-primary conservator does not have possession',
        summer: 'Thirty consecutive days during summer months with 60 days written notice'
      },
      expanded: {
        description: 'Available when parents live within 100 miles of each other',
        weekends: 'First, third, and fifth weekends from Friday when school is dismissed to Monday morning when school resumes',
        weekdays: 'Thursday from when school is dismissed to Friday morning when school resumes',
        additionalTime: 'Additional time during school holidays and breaks'
      },
      longDistance: {
        description: 'For parents living more than 100 miles apart',
        weekends: 'One weekend per month',
        summer: 'Extended summer possession up to 42 consecutive days',
        holidays: 'Alternating major holidays with extended periods'
      },
      holidays: {
        description: 'Specific holiday possession schedules',
        oddYears: [
          'Thanksgiving (Wednesday 6:00 PM to Sunday 6:00 PM)',
          'Christmas Eve and Christmas Day until December 28 at noon',
          'Spring break'
        ],
        evenYears: [
          'Christmas break from December 28 at noon to 6:00 PM on the day before school resumes',
          'Easter weekend',
          'Memorial Day weekend',
          'July 4th weekend',
          'Labor Day weekend'
        ],
        annual: [
          'Father\'s Day weekend (to father)',
          'Mother\'s Day weekend (to mother)',
          'Child\'s birthday'
        ]
      }
    },
    resources: {
      courts: [
        {
          name: 'Texas Family Law Courts Information',
          url: 'https://www.txcourts.gov/programs-services/family-court/',
          description: 'Official Texas court system family law information and resources'
        },
        {
          name: 'Texas Family Law Forms',
          url: 'https://www.txcourts.gov/rules-forms/forms/',
          description: 'Official Texas family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Harris County Family Law Center',
          address: '201 Caroline Street, Houston, TX 77002',
          phone: '(713) 755-6405',
          description: 'Family court serving Houston and Harris County'
        },
        {
          name: 'Dallas County Family Courts',
          address: '600 Commerce Street, Dallas, TX 75202',
          phone: '(214) 653-7447',
          description: 'Family courts serving Dallas County'
        },
        {
          name: 'Bexar County Family Court',
          address: '300 Dolorosa Street, San Antonio, TX 78205',
          phone: '(210) 335-2011',
          description: 'Family court serving San Antonio and Bexar County'
        },
        {
          name: 'Travis County Family Courts',
          address: '1000 Guadalupe Street, Austin, TX 78701',
          phone: '(512) 854-9302',
          description: 'Family courts serving Austin and Travis County'
        }
      ],
      support: [
        {
          name: 'Texas Attorney General Child Support Division',
          url: 'https://www.texasattorneygeneral.gov/child-support',
          phone: '1-800-252-8014',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Texas Child Support Calculator',
          url: 'https://csapps.oag.texas.gov/monthly-child-support-calculator',
          description: 'Official Texas child support calculator for estimating support amounts'
        },
        {
          name: 'Texas Legal Aid Organizations',
          url: 'https://www.texaslawhelp.org/',
          description: 'Statewide directory of legal aid organizations providing free legal services'
        },
        {
          name: 'State Bar of Texas Family Law Section',
          url: 'https://www.texasbar.com/sections/family-law',
          description: 'Professional resources and directory of family law attorneys in Texas'
        },
        {
          name: 'Texas Council on Family Violence',
          url: 'https://tcfv.org/',
          phone: '1-800-525-1978',
          description: 'Statewide domestic violence resources and legal advocacy services'
        }
      ],
      legal: [
        {
          name: 'State Bar of Texas Lawyer Referral Service',
          url: 'https://www.texasbar.com/AM/Template.cfm?Section=Lawyer_Referral_Service_LRIS_',
          phone: '1-800-252-9690',
          description: 'Professional attorney referrals for family law cases throughout Texas'
        },
        {
          name: 'University of Texas School of Law Family Law Clinic',
          url: 'https://law.utexas.edu/clinics/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Texas Collaborative Law Council',
          url: 'https://www.collaborativelaw.org/find-a-professional/texas/',
          description: 'Network of collaborative divorce attorneys and professionals in Texas'
        },
        {
          name: 'Texas Association of Mediators',
          url: 'https://www.txmediator.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        }
      ],
      educational: [
        {
          name: 'Texas Children First Program',
          url: 'https://www.txcourts.gov/programs-services/family-court/children-first/',
          description: 'Parent education program for divorcing parents with minor children'
        },
        {
          name: 'Texas Parenting Plan Resources',
          url: 'https://www.texaslawhelp.org/article/parenting-plans-texas',
          description: 'Comprehensive guide to creating effective parenting plans in Texas'
        },
        {
          name: 'Texas Guardian ad Litem Programs',
          url: 'https://www.txcourts.gov/programs-services/family-court/guardian-ad-litem/',
          description: 'Information about guardian ad litem and attorney ad litem services for children'
        },
        {
          name: 'Texas A&M AgriLife Extension - Family Life',
          url: 'https://agrilifeextension.tamu.edu/browse/featured-solutions/families-communities/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Harris County District Courts',
            address: '201 Caroline Street, Houston, TX 77002',
            phone: '(713) 755-6405',
            jurisdiction: 'Harris County - Houston metropolitan area'
          },
          {
            name: 'Dallas County District Courts',
            address: '600 Commerce Street, Dallas, TX 75202',
            phone: '(214) 653-7447',
            jurisdiction: 'Dallas County - Dallas metropolitan area'
          },
          {
            name: 'Bexar County District Courts',
            address: '300 Dolorosa Street, San Antonio, TX 78205',
            phone: '(210) 335-2011',
            jurisdiction: 'Bexar County - San Antonio metropolitan area'
          },
          {
            name: 'Travis County District Courts',
            address: '1000 Guadalupe Street, Austin, TX 78701',
            phone: '(512) 854-9302',
            jurisdiction: 'Travis County - Austin metropolitan area'
          },
          {
            name: 'Tarrant County District Courts',
            address: '401 West Belknap Street, Fort Worth, TX 76196',
            phone: '(817) 884-1265',
            jurisdiction: 'Tarrant County - Fort Worth metropolitan area'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$300-400 (varies by county)',
          'Divorce (without children)': '$300-400 (varies by county)',
          'Suit Affecting Parent-Child Relationship (SAPCR)': '$300-400 (varies by county)',
          'Child Support': '$45-100 (varies by county)',
          'Modification': '$300-400 (varies by county)',
          'Paternity': '$300-400 (varies by county)',
          'Protective Order': 'No fee'
        },
        requiredForms: [
          'Original Petition',
          'Citation',
          'Standing Order',
          'Financial Information Forms',
          'Parenting Plan',
          'Child Support Worksheets (if applicable)',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Texas Family Code',
          url: 'https://statutes.capitol.texas.gov/Docs/FA/htm/FA.1.htm',
          description: 'Primary statutes governing family law, divorce, and conservatorship in Texas'
        },
        {
          name: 'Texas Child Support Guidelines (Family Code Chapter 154)',
          url: 'https://statutes.capitol.texas.gov/Docs/FA/htm/FA.154.htm',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Texas Standard Possession Order (Family Code § 153.312)',
          url: 'https://statutes.capitol.texas.gov/Docs/FA/htm/FA.153.htm#153.312',
          description: 'Detailed possession and access schedules for Texas'
        }
      ],
      emergencyContacts: [
        {
          name: 'Texas Family Violence Hotline',
          phone: '1-800-799-7233',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Texas Child Abuse Hotline',
          phone: '1-800-252-5400',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Texas Crisis Text Line',
          phone: 'Text HOME to 741741',
          description: '24/7 crisis support via text message'
        }
      ]
    },
    bestInterestsFactors: [
      'The desires of the child',
      'The emotional and physical needs of the child now and in the future',
      'The emotional and physical danger to the child now and in the future',
      'The parental abilities of the individuals seeking custody',
      'The programs available to assist these individuals to promote the best interest of the child',
      'The plans for the child by these individuals or by the agency seeking custody',
      'The stability of the home or proposed placement',
      'The acts or omissions of the parent which may indicate that the existing parent-child relationship is not a proper one',
      'Any excuse for the acts or omissions of the parent'
    ],
    practicalTips: [
      'Familiarize yourself with Texas\' unique conservatorship terminology and Standard Possession Order',
      'Document all possession exchanges and any violations of court orders',
      'Complete mandatory mediation in good faith - Texas courts require it in most contested cases',
      'Texas is a large state - consider geography and travel time when planning possession schedules',
      'Keep detailed records of expenses related to the children for potential support issues',
      'Understand that Joint Managing Conservatorship is presumed to be in the child\'s best interest',
      'Be prepared to address geographic restrictions if you plan to relocate',
      'Texas courts favor parents who encourage the child\'s relationship with the other parent'
    ],
    mediation: {
      description: 'Texas requires mediation in most contested conservatorship cases',
      requirements: [
        'Mandatory before trial in contested custody cases',
        'Both parties must attend and participate in good faith',
        'Mediator must be agreed upon or court-appointed',
        'Confidential process - statements cannot be used in court'
      ],
      benefits: [
        'Less expensive than going to trial',
        'Faster resolution of disputes',
        'Parents maintain more control over outcomes',
        'Less adversarial than court proceedings',
        'Can preserve co-parenting relationships'
      ],
      process: [
        'Court orders mediation or parties agree to mediate',
        'Select qualified mediator experienced in family law',
        'Attend mediation session with or without attorneys',
        'Work toward agreement on conservatorship and possession',
        'Any agreement reached is put in writing and submitted to court'
      ]
    },
    alternatives: {
      description: 'Texas recognizes several alternatives to traditional litigation',
      options: [
        {
          name: 'Collaborative Law',
          description: 'Both parties hire specially trained collaborative attorneys and work together to reach agreements'
        },
        {
          name: 'Mediation',
          description: 'Neutral third party helps parents negotiate agreements outside of court'
        },
        {
          name: 'Arbitration',
          description: 'Private judge makes binding decisions on disputed issues'
        },
        {
          name: 'Parenting Coordination',
          description: 'Court-appointed professional helps high-conflict parents implement parenting plans'
        }
      ],
      advantages: [
        'Generally less expensive than litigation',
        'Faster resolution than going through trial',
        'More privacy and confidentiality',
        'Parents retain more control over outcomes',
        'Less trauma for children',
        'Can preserve working relationships between parents'
      ]
    }
  }
};

export default texasData;